import is from '@sindresorhus/is';
import Result from 'true-myth/result';
import { z } from 'zod';

import {
    booleanTypeLiteralSchema,
    dateTypeLiteralSchema,
    integerTypeLiteralSchema,
    jobTypeLiteralSchema,
    percentTypeLiteralSchema,
    searchTypeLiteralSchema,
    skillTypeLiteralSchema,
    stateTypeLiteralSchema,
    stringTypeLiteralSchema,
    userTypeLiteralSchema,
    uuidTypeLiteralSchema,
} from './filter-response';
import { jobSeekingStatusSchema, relocationPreferenceSchema } from './user';

import type { Translate } from '../internationalization/translation';

const filterBaseSchema = z.object({
    field: z.string(),
    label: z.string(),
});

export const filterStateOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
});

export type FilterStateOption = z.infer<typeof filterStateOptionSchema>;

export const filterStateOptionsSchema = filterStateOptionSchema.array();

export const filterStateComponentMultiChipSelectLiteralSchema = z.literal('MultiChipSelect');

const componentTypeSchema = z.literal('Autocomplete').or(filterStateComponentMultiChipSelectLiteralSchema);

export const filterStateStringSchema = filterBaseSchema.extend({
    component: componentTypeSchema.optional(),
    options: filterStateOptionsSchema,
    type: stringTypeLiteralSchema,
    value: z.string().array().optional(),
});

export type FilterStateString = z.infer<typeof filterStateStringSchema>;

export const filterStateIntegerSchema = filterBaseSchema.extend({
    component: componentTypeSchema.optional(),
    options: filterStateOptionsSchema,
    type: integerTypeLiteralSchema,
    value: z.string().array().optional(),
});

export type FilterStateInteger = z.infer<typeof filterStateIntegerSchema>;

export const filterStateBooleanSchema = filterBaseSchema.extend({
    type: booleanTypeLiteralSchema,
    value: z.boolean().optional(),
});

export type FilterStateBoolean = z.infer<typeof filterStateBooleanSchema>;

export const filterStateSkillValueSchema = z.uuid().array();

export const filterStateSkillSchema = filterBaseSchema.extend({
    type: skillTypeLiteralSchema,
    value: filterStateSkillValueSchema.optional(),
});

export type FilterStateSkills = z.infer<typeof filterStateSkillSchema>;

export const filterStatePercentageValueSchema = z.number().min(0).max(1);

export const filterStatePercentageRangeSchema = z.tuple([
    filterStatePercentageValueSchema,
    filterStatePercentageValueSchema,
]);

export const filterStatePercentageSchema = filterBaseSchema.extend({
    type: percentTypeLiteralSchema,
    value: filterStatePercentageRangeSchema.optional(),
});

export type FilterStatePercentage = z.infer<typeof filterStatePercentageSchema>;

export const filterStateJobSchema = filterBaseSchema.extend({
    type: jobTypeLiteralSchema,
    value: z.string().array().optional(),
});

export const filterStateDateRangeSchema = z.tuple([z.date().nullable(), z.date().nullable()]);

export const filterStateDateRangeFromValueStoreSchema = z
    .tuple([z.string().nullable(), z.string().nullable()])
    .transform((dateRange) => dateRange.map((startOrEnd) => (is.string(startOrEnd) ? new Date(startOrEnd) : null)));

export const filterStateDateSchema = filterBaseSchema.extend({
    type: dateTypeLiteralSchema,
    value: filterStateDateRangeSchema.optional(),
});

export type FilterStateDate = z.infer<typeof filterStateDateSchema>;

export const filterStateUserSchema = filterBaseSchema.extend({
    options: filterStateOptionsSchema,
    type: userTypeLiteralSchema,
    value: z.uuid().array().optional(),
});

export type FilterStateUser = z.infer<typeof filterStateUserSchema>;

export const filterStateUuidSchema = filterBaseSchema.extend({
    type: uuidTypeLiteralSchema,
    value: z.uuid().array().optional(),
});

export const filterStateSearchSchema = filterBaseSchema.extend({
    type: searchTypeLiteralSchema,
    value: z.tuple([z.string()]).optional(),
});

export const filterStateStateSchema = filterBaseSchema.extend({
    type: stateTypeLiteralSchema,
    value: z.tuple([z.string()]).optional(),
});

export const filterStateSchema = z.discriminatedUnion('type', [
    filterStateBooleanSchema,
    filterStateDateSchema,
    filterStateIntegerSchema,
    filterStateJobSchema,
    filterStatePercentageSchema,
    filterStateSearchSchema,
    filterStateSkillSchema,
    filterStateStateSchema,
    filterStateStringSchema,
    filterStateUserSchema,
    filterStateUuidSchema,
]);

export type FilterState = z.infer<typeof filterStateSchema>;

export type FilterWithFilterStates = {
    readonly id: string;
    readonly filterStates: readonly PreDefinedOrCustomFilterState[];
    readonly subTitle: string;
    readonly title: string;
};

export const createdAtLiteralSchema = z.literal('createdAt');
export const creatorLiteralSchema = z.literal('creator');
export const departmentLiteralSchema = z.literal('department');
export const durationLiteralSchema = z.literal('duration');
export const hiringManagerLiteralSchema = z.literal('hiringManager');
export const jobSeekingLiteralSchema = z.literal('jobSeeking');
export const jobTitleLiteralSchema = z.literal('jobTitle');
export const ownerLiteralSchema = z.literal('owner');
export const reviewerLiteralSchema = z.literal('reviewer');
export const reviewStateLiteralSchema = z.literal('reviewState');
export const providerLiteralSchema = z.literal('provider');
export const publishedAtLiteralSchema = z.literal('publishedAt');
export const relocationPreferenceLiteralSchema = z.literal('relocationPreference');
export const seniorityLevelLiteralSchema = z.literal('seniorityLevel');
export const startDateLiteralSchema = z.literal('startDate');
export const updatedAtLiteralSchema = z.literal('updatedAt');
export const workPlaceLiteralSchema = z.literal('workPlace');
export const skillTypeTypeLiteralSchema = z.literal('skillType');
export const skillGapRelativeLiteralSchema = z.literal('skillGapRelative');

export const seniorityLevelFilterStateSchema = filterStateIntegerSchema.extend({
    field: seniorityLevelLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: z.union([
                z.literal('0'),
                z.literal('1'),
                z.literal('2'),
                z.literal('3'),
                z.literal('4'),
                z.literal('5'),
                z.literal('6'),
                z.literal('7'),
                z.literal('8'),
                z.literal('9'),
                z.literal('10'),
            ]),
        })
        .array(),
});

export const relocationPreferenceFilterStateSchema = filterStateStringSchema.extend({
    field: relocationPreferenceLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: relocationPreferenceSchema,
        })
        .array(),
});

export const jobSeekingFilterStateSchema = filterStateStringSchema.extend({
    field: jobSeekingLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: jobSeekingStatusSchema,
        })
        .array(),
});

export const departmentFilterStateSchema = filterStateStringSchema.extend({
    field: departmentLiteralSchema,
});

export const jobTitleFilterStateSchema = filterStateStringSchema.extend({
    field: jobTitleLiteralSchema,
});

export const providerFilterStateSchema = filterStateStringSchema.extend({
    field: providerLiteralSchema,
});

export const updatedAtFilterStateSchema = filterStateDateSchema.extend({
    field: updatedAtLiteralSchema,
});

export const createdAtFilterStateSchema = filterStateDateSchema.extend({
    field: createdAtLiteralSchema,
});

export const skillFilterStateSchema = filterStateSkillSchema.extend({
    field: skillTypeLiteralSchema,
});

export const skillTypeFilterStateSchema = filterStateStringSchema.extend({
    field: skillTypeTypeLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: z.union([z.literal('hard'), z.literal('soft'), z.literal('language')]),
        })
        .array(),
});

export const skillGapRelativeFilterStateSchema = filterStatePercentageSchema.extend({
    field: skillGapRelativeLiteralSchema,
});

export const workSpaceStatusSchema = z.union([z.literal('on-site'), z.literal('remote')]);

export const workPlaceFilterStateSchema = filterStateStringSchema.extend({
    field: workPlaceLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: workSpaceStatusSchema,
        })
        .array(),
});

export const durationStatusSchema = z.union([z.literal('fixed-term'), z.literal('permanent')]);

export const durationFilterStateSchema = filterStateStringSchema.extend({
    field: durationLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: durationStatusSchema,
        })
        .array(),
});

export const ownerFilterStateSchema = filterStateUserSchema.extend({
    field: ownerLiteralSchema,
});

export type OwnerFilterState = z.infer<typeof ownerFilterStateSchema>;

export const reviewerFilterStateSchema = filterStateUserSchema.extend({
    field: reviewerLiteralSchema,
});

export const reviewStateFilterSchema = z.union([z.literal('assigned'), z.literal('approved')]);

export const reviewStateFilterStateSchema = filterStateStringSchema.extend({
    field: reviewStateLiteralSchema,
    options: filterStateOptionSchema
        .extend({
            id: reviewStateFilterSchema,
        })
        .array(),
});

export const hiringManagerFilterStateSchema = filterStateUserSchema.extend({
    field: hiringManagerLiteralSchema,
});

export const creatorFilterStateSchema = filterStateUserSchema.extend({
    field: creatorLiteralSchema,
});

export const startDateFilterStateSchema = filterStateDateSchema.extend({
    field: startDateLiteralSchema,
});

export const publishedAtFilterStateSchema = filterStateDateSchema.extend({
    field: publishedAtLiteralSchema,
});

export const customFilterKeySchema = z.custom<`custom.${string}`>((value) =>
    z.string().parse(value).startsWith('custom.'),
);

export const customFilterStateSchema = z.discriminatedUnion('type', [
    filterStateBooleanSchema.extend({ field: customFilterKeySchema }),
    filterStateDateSchema.extend({ field: customFilterKeySchema }),
    filterStateIntegerSchema.extend({ field: customFilterKeySchema }),
    filterStatePercentageSchema.extend({ field: customFilterKeySchema }),
    filterStateSkillSchema.extend({ field: customFilterKeySchema }),
    filterStateStringSchema.extend({ field: customFilterKeySchema }),
    filterStateUserSchema.extend({ field: customFilterKeySchema }),
    filterStateUuidSchema.extend({ field: customFilterKeySchema }),
]);

export const createCustomFilterSchema = ({
    attributeNamespace,
    translate,
}: {
    readonly attributeNamespace: 'jobProfile' | 'jobs' | 'learningOpportunity' | 'user';
    readonly translate: Translate;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
    return customFilterStateSchema.transform((filter) => {
        const fieldName = filter.field.replace('custom.', '');
        const transformedFilter = { ...filter, label: translate(`attributes.${attributeNamespace}.${fieldName}`) };

        if ('options' in transformedFilter) {
            return {
                ...transformedFilter,
                options: transformedFilter.options.map((option) => ({
                    ...option,
                    label: translate(`attributes.${attributeNamespace}.${fieldName}-options.${option.id}`, {
                        defaultValue: option.label,
                    }),
                })),
            };
        }

        return transformedFilter;
    });
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createPreDefinedFilterSchemas = ({ translate }: { readonly translate: Translate }) => {
    const component = filterStateComponentMultiChipSelectLiteralSchema.value;

    const seniorityLabel = translate('pages.jobs.columns.seniority.title');
    const seniorityLevelSchemaTransformed = seniorityLevelFilterStateSchema
        .transform((seniorityLevel) => ({
            ...seniorityLevel,
            component,
            label: seniorityLabel,
            options: seniorityLevel.options
                .filter(({ id }) => id !== '0')
                .map((option) => ({
                    ...option,
                    label: translate(`common.enums.seniority-level.${option.id}`),
                }))
                .reverse(),
        }))
        .catch({
            component,
            field: seniorityLevelLiteralSchema.value,
            label: seniorityLabel,
            options: [],
            type: integerTypeLiteralSchema.value,
        });

    const relocationLabel = translate('pages.job-detail.recommendations.relocation-preference.title');
    const relocationPreferenceOptionsSchemaTransformed = relocationPreferenceFilterStateSchema
        .transform((relocationPreference) => ({
            ...relocationPreference,
            component,
            label: relocationLabel,
            options: relocationPreference.options.map((option) => ({
                ...option,
                label: translate(`common.enums.relocation-preference.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: relocationPreferenceLiteralSchema.value,
            label: relocationLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const departmentLabel = translate('pages.edit-profile-details.department');
    const departmentSchemaTransformed = departmentFilterStateSchema
        .transform((department) => ({
            ...department,
            label: departmentLabel,
        }))
        .catch({
            field: departmentLiteralSchema.value,
            label: departmentLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const jobTitleLabel = translate('pages.edit-profile-details.job-title');
    const jobTitleSchemaTransformed = jobTitleFilterStateSchema
        .transform((jobTitle) => ({
            ...jobTitle,
            label: jobTitleLabel,
        }))
        .catch({
            field: jobTitleLiteralSchema.value,
            label: jobTitleLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const jobSeekingLabel = translate('pages.job-detail.recommendations.job-seeking.title');
    const jobSeekingSchemaTransformed = jobSeekingFilterStateSchema
        .transform((jobSeeking) => ({
            ...jobSeeking,
            component,
            label: jobSeekingLabel,
            options: jobSeeking.options.map((option) => ({
                ...option,
                label: translate(`common.enums.job-seeking-status.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: jobSeekingLiteralSchema.value,
            label: jobSeekingLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const durationLabel = translate('pages.jobs.columns.duration.title');
    const durationSchemaTransformed = durationFilterStateSchema
        .transform((duration) => ({
            ...duration,
            component,
            label: durationLabel,
            options: duration.options.map((option) => ({
                ...option,
                label: translate(`common.enums.duration.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: durationLiteralSchema.value,
            label: durationLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const reviewStateLabel = translate('pages.job-architecture.verified-status');
    const reviewStateSchemaTransformed = reviewStateFilterStateSchema
        .transform((reviewState) => ({
            ...reviewState,
            component,
            label: reviewStateLabel,
            options: reviewState.options.map((option) => ({
                ...option,
                label: translate(`common.enums.job-profile-review-state.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: reviewStateLiteralSchema.value,
            label: reviewStateLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const workPlaceLabel = translate('common.enums.work-place.remote');
    const workPlaceSchemaTransformed = workPlaceFilterStateSchema
        .transform((workPlace) => ({
            ...workPlace,
            component,
            label: workPlaceLabel,
            options: workPlace.options.map((option) => ({
                ...option,
                label: translate(`common.enums.work-place.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: workPlaceLiteralSchema.value,
            label: workPlaceLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const providerLabel = translate('pages.learning-opportunities.headlines.provider');
    const providerSchemaTransformed = providerFilterStateSchema
        .transform((provider) => ({
            ...provider,
            component,
            label: providerLabel,
            options: provider.options.map((option) => ({
                ...option,
                label: translate(`providers.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: providerLiteralSchema.value,
            label: providerLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const updatedAtLabel = translate('pages.learning-opportunities.headlines.updated-at');
    const updatedAtSchemaTransformed = updatedAtFilterStateSchema
        .transform((updatedAt) => ({
            ...updatedAt,
            label: updatedAtLabel,
        }))
        .catch({
            field: updatedAtLiteralSchema.value,
            label: updatedAtLabel,
            type: dateTypeLiteralSchema.value,
        });

    const createdAtLabel = translate('pages.learning-opportunities.headlines.created');
    const createdAtSchemaTransformed = createdAtFilterStateSchema
        .transform((createdAt) => ({
            ...createdAt,
            label: createdAtLabel,
        }))
        .catch({
            field: createdAtLiteralSchema.value,
            label: createdAtLabel,
            type: dateTypeLiteralSchema.value,
        });

    const skillLabel = translate('pages.learning-opportunities.filters.skills-preferences.selected-skills');
    const skillSchemaTransformed = skillFilterStateSchema
        .transform((skill) => ({
            ...skill,
            label: skillLabel,
        }))
        .catch({
            field: skillTypeLiteralSchema.value,
            label: skillLabel,
            type: skillTypeLiteralSchema.value,
        });

    const skillTypeLabel = translate('common.skill');
    const skillTypeSchemaTransformed = skillTypeFilterStateSchema
        .transform((skillType) => ({
            ...skillType,
            component,
            label: skillTypeLabel,
            options: skillType.options.map((option) => ({
                ...option,
                label: translate(`common.enums.skillType.${option.id}`),
            })),
        }))
        .catch({
            component,
            field: skillTypeTypeLiteralSchema.value,
            label: skillTypeLabel,
            options: [],
            type: stringTypeLiteralSchema.value,
        });

    const skillGapRelativeLabel = translate('common.skill');
    const skillGapRelativeSchemaTransformed = skillGapRelativeFilterStateSchema
        .transform((skillGapRelative) => ({
            ...skillGapRelative,
            label: skillGapRelativeLabel,
        }))
        .catch({
            field: skillGapRelativeLiteralSchema.value,
            label: skillGapRelativeLabel,
            type: percentTypeLiteralSchema.value,
        });

    const ownerLabel = translate('pages.jobs.columns.owner');
    const ownerSchemaTransformed = ownerFilterStateSchema
        .transform((owner) => ({
            ...owner,
            label: ownerLabel,
        }))
        .catch({
            field: ownerLiteralSchema.value,
            label: ownerLabel,
            options: [],
            type: userTypeLiteralSchema.value,
        });

    const reviewerLabel = translate('pages.job-architecture.review-by');
    const reviewerSchemaTransformed = reviewerFilterStateSchema
        .transform((reviewer) => ({
            ...reviewer,
            label: reviewerLabel,
        }))
        .catch({
            field: reviewerLiteralSchema.value,
            label: reviewerLabel,
            options: [],
            type: userTypeLiteralSchema.value,
        });

    const hiringManagerLabel = translate('pages.jobs.columns.hiring-manager');
    const hiringManagerSchemaTransformed = hiringManagerFilterStateSchema
        .transform((hiringManager) => ({
            ...hiringManager,
            label: hiringManagerLabel,
        }))
        .catch({
            field: hiringManagerLiteralSchema.value,
            label: hiringManagerLabel,
            options: [],
            type: userTypeLiteralSchema.value,
        });

    const creatorLabel = translate('pages.jobs.columns.creator');
    const creatorSchemaTransformed = creatorFilterStateSchema
        .transform((creator) => ({
            ...creator,
            label: creatorLabel,
        }))
        .catch({
            field: creatorLiteralSchema.value,
            label: creatorLabel,
            options: [],
            type: userTypeLiteralSchema.value,
        });

    const startDateLabel = translate('pages.jobs.columns.start-date');
    const startDateSchemaTransformed = startDateFilterStateSchema
        .transform((startDate) => ({
            ...startDate,
            label: startDateLabel,
        }))
        .catch({
            field: startDateLiteralSchema.value,
            label: startDateLabel,
            type: dateTypeLiteralSchema.value,
        });

    const publishedAtLabel = translate('pages.jobs.columns.publish-date');
    const publishedAtSchemaTransformed = publishedAtFilterStateSchema
        .transform((publishedAt) => ({
            ...publishedAt,
            label: publishedAtLabel,
        }))
        .catch({
            field: publishedAtLiteralSchema.value,
            label: publishedAtLabel,
            type: dateTypeLiteralSchema.value,
        });

    return {
        createdAt: createdAtSchemaTransformed,
        creator: creatorSchemaTransformed,
        department: departmentSchemaTransformed,
        duration: durationSchemaTransformed,
        hiringManager: hiringManagerSchemaTransformed,
        jobSeeking: jobSeekingSchemaTransformed,
        jobTitle: jobTitleSchemaTransformed,
        owner: ownerSchemaTransformed,
        provider: providerSchemaTransformed,
        publishedAt: publishedAtSchemaTransformed,
        relocationPreference: relocationPreferenceOptionsSchemaTransformed,
        reviewer: reviewerSchemaTransformed,
        reviewState: reviewStateSchemaTransformed,
        seniorityLevel: seniorityLevelSchemaTransformed,
        skill: skillSchemaTransformed,
        skillGapRelative: skillGapRelativeSchemaTransformed,
        skillType: skillTypeSchemaTransformed,
        startDate: startDateSchemaTransformed,
        updatedAt: updatedAtSchemaTransformed,
        workPlace: workPlaceSchemaTransformed,
    } as const;
};

const preDefinedFilterStateSchema = z.union([
    createdAtFilterStateSchema,
    creatorFilterStateSchema,
    departmentFilterStateSchema,
    durationFilterStateSchema,
    hiringManagerFilterStateSchema,
    jobSeekingFilterStateSchema,
    jobTitleFilterStateSchema,
    ownerFilterStateSchema,
    reviewerFilterStateSchema,
    reviewStateFilterStateSchema,
    providerFilterStateSchema,
    publishedAtFilterStateSchema,
    relocationPreferenceFilterStateSchema,
    seniorityLevelFilterStateSchema,
    skillFilterStateSchema,
    skillGapRelativeFilterStateSchema,
    skillTypeFilterStateSchema,
    startDateFilterStateSchema,
    updatedAtFilterStateSchema,
    workPlaceFilterStateSchema,
]);

type PreDefinedFilterState = z.infer<typeof preDefinedFilterStateSchema>;

export const preDefinedOrCustomFilterSchema = preDefinedFilterStateSchema.or(customFilterStateSchema);

export type PreDefinedOrCustomFilterState = z.infer<typeof preDefinedOrCustomFilterSchema>;

export const preDefinedFilterFieldNameSchema = z.union([
    createdAtLiteralSchema,
    creatorLiteralSchema,
    departmentLiteralSchema,
    durationLiteralSchema,
    hiringManagerLiteralSchema,
    jobSeekingLiteralSchema,
    jobTitleLiteralSchema,
    ownerLiteralSchema,
    reviewerLiteralSchema,
    reviewStateLiteralSchema,
    providerLiteralSchema,
    publishedAtLiteralSchema,
    relocationPreferenceLiteralSchema,
    seniorityLevelLiteralSchema,
    skillGapRelativeLiteralSchema,
    skillTypeLiteralSchema,
    skillTypeTypeLiteralSchema,
    startDateLiteralSchema,
    updatedAtLiteralSchema,
    workPlaceLiteralSchema,
]);

export const preDefinedOrCustomFilterFieldNameSchema = preDefinedFilterFieldNameSchema.or(customFilterKeySchema);

export const createSortFilterStateByOrder =
    ({ filterOrder }: { readonly filterOrder: readonly PreDefinedFilterState['field'][] }) =>
    (a: PreDefinedOrCustomFilterState, b: PreDefinedOrCustomFilterState): number => {
        const aResult = preDefinedFilterStateSchema.safeParse(a);
        const bResult = preDefinedFilterStateSchema.safeParse(b);
        const aIndex = aResult.success ? filterOrder.indexOf(aResult.data.field) : -1;
        const bIndex = bResult.success ? filterOrder.indexOf(bResult.data.field) : -1;

        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        if (aIndex !== -1) {
            return -1;
        }

        if (bIndex !== -1) {
            return 1;
        }

        return 0;
    };

export type FilterFieldValueMap<T extends PreDefinedOrCustomFilterState = PreDefinedOrCustomFilterState> = Partial<
    Readonly<Record<T['field'], any>>
>;

// eslint-disable-next-line complexity
const mapFilterValue = (filter: FilterState, filterValueFromUrl: unknown): FilterFieldValueMap => {
    const { field, type } = filter;

    if (type === 'boolean') {
        const value = z.boolean().safeParse(filterValueFromUrl);

        return {
            [field]: value.success ? value.data : undefined,
        };
    }

    if (type === 'date') {
        const value = filterStateDateRangeFromValueStoreSchema.safeParse(filterValueFromUrl);

        return {
            [field]: value.success ? value.data : undefined,
        };
    }

    if (type === 'percent') {
        const value = filterStatePercentageRangeSchema.safeParse(filterValueFromUrl);

        return {
            [field]: value.success ? value.data : undefined,
        };
    }

    if (type === 'skill') {
        const value = z.uuid().array().safeParse(filterValueFromUrl);

        return {
            [field]: value.success ? value.data : undefined,
        };
    }

    if ('options' in filter) {
        const value = z
            .union([z.literal(null), z.literal(null), ...filter.options.map(({ id }) => z.literal(id))])
            .array()
            .safeParse(filterValueFromUrl);

        return {
            [field]: value.success ? value.data : undefined,
        };
    }

    return {};
};

export const reduceToFilterStateFieldValuePairs = ({
    filterStateKeyValue,
    filterStates,
}: {
    readonly filterStateKeyValue: Record<string, unknown>;
    readonly filterStates: readonly PreDefinedOrCustomFilterState[];
}): FilterFieldValueMap =>
    filterStates.reduce<FilterFieldValueMap>((filterStates, filterState) => {
        const filterValueFromUrl = filterStateKeyValue[filterState.field];
        if (is.nullOrUndefined(filterValueFromUrl)) {
            return filterStates;
        }

        return {
            ...filterStates,
            ...mapFilterValue(filterState, filterValueFromUrl),
        };
    }, {});

const filterKeyValueSchema = z.record(z.string(), z.record(z.string(), z.unknown()));

type FilterKeyValue = z.infer<typeof filterKeyValueSchema>;

export const getFilterValuesFromFilterValue = ({
    filterKeyValue,
    filters,
}: {
    readonly filterKeyValue: FilterKeyValue;
    readonly filters: readonly FilterWithFilterStates[];
}): Record<string, FilterFieldValueMap> =>
    Object.keys(filterKeyValue).reduce((acc, filterId) => {
        const filter = filters.find(({ id }) => id === filterId);
        const filterStateKeyValue = filterKeyValue[filterId];

        if (is.undefined(filter) || is.undefined(filterStateKeyValue)) {
            return acc;
        }

        return {
            ...acc,
            [filterId]: reduceToFilterStateFieldValuePairs({
                filterStateKeyValue,
                filterStates: filter.filterStates,
            }),
        };
    }, {});

export const setFilterStateValuesToFilter = (
    filters: readonly FilterWithFilterStates[],
    filterKeyValuePairs: Record<string, FilterFieldValueMap>,
): readonly FilterWithFilterStates[] =>
    filters.map((filter) => {
        const filterValues = filterKeyValuePairs[filter.id];

        if (!is.undefined(filterValues)) {
            return {
                ...filter,
                filterStates: filter.filterStates.map((filterState) => ({
                    ...filterState,
                    value: filterValues[filterState.field],
                })),
            };
        }

        return filter;
    });

export const getFilterValuesFromFilterValueString = ({
    filters,
    filterValuesJsonString,
}: {
    readonly filters: readonly FilterWithFilterStates[];
    readonly filterValuesJsonString: string;
}): Result<Record<string, FilterFieldValueMap>, Error> => {
    const filterValues = z
        .string()
        .transform((value) => {
            const filterKeyValue = filterKeyValueSchema.parse(JSON.parse(value));

            return getFilterValuesFromFilterValue({ filterKeyValue, filters });
        })
        .safeParse(filterValuesJsonString);

    return filterValues.success ? Result.ok(filterValues.data) : Result.err(filterValues.error);
};

export const reduceFiltersToKeyValuePairs = (
    filters: readonly FilterWithFilterStates[],
): Record<string, Record<string, unknown>> =>
    filters.reduce((reducedFilters, { id, filterStates }) => {
        const reduceFilterStates = filterStates.reduce((reducedFilterStates, { field, value }) => {
            if (is.nullOrUndefined(value) || is.emptyArray(value)) {
                return reducedFilterStates;
            }

            return {
                ...reducedFilterStates,
                [field]: value,
            };
        }, {});

        if (is.nonEmptyObject(reduceFilterStates)) {
            return {
                ...reducedFilters,
                [id]: reduceFilterStates,
            };
        }

        return reducedFilters;
    }, {});
